<template>
  <nav
    v-if="state !== 'error'"
    class="prod-swatches"
    :class="{ 'is-loading': state !== 'ready' }"
  >
    <ul class="prod-swatches__list">
      <li
        v-for="(style, index) in linkedStyles"
        :key="getStyleKey(style, index)"
        class="prod-swatches__item"
        :class="getSwatchClasses(style)"
        :style="`animation-delay: ${index * 0.1}s`"
      >
        <component
          :is="elementType"
          class="prod-swatches__button"
          :disabled="getDisabledState(style)"
          :href="getSwatchLink(style)"
          :type="getButtonType(style)"
          :aria-label="getSwatchButtonText(style)"
          @click="handleSwatchClick(style)"
          @mouseover="handleSwatchHover(style)"
          @mouseleave="handleSwatchHover"
        >
          <img
            :alt="getImageAlt(style)"
            class="prod-swatches__img"
            :src="getSwatchImageUrl(style)"
            height="20"
            width="20"
          >
        </component>
      </li>
    </ul>
  </nav>
</template>

<script>

/**
 * Component: Product Swatches
 * ------------------------------------------------------------------------------
 * - Reusable component for product swatches.
 * @param {string} [elementType] - Type of swatch element; button or <a> tag.
 * @param {Array} linkedStyles - Linked Styles from a formatted product object.
 * @param {string} productHandle - Handle of the current product being rendered.
 */
import { getSizedImageUrl } from '~/helpers/format'

export default {
  name: 'ProductSwatches',

  props: {
    elementType: {
      type: String,
      default: 'a',
    },
    linkedStyles: {
      type: Array,
      default: () => [
        {
          url: 0,
        },
        {
          url: 1,
        },
        {
          url: 2,
        },
      ],
      required: true,
    },
    productHandle: {
      type: String,
      default: '',
    },
  },

  emits: [
    'swatch-image-clicked',
    'swatch-image-hover',
  ],

  data() {
    return {
      state: 'loading',
    }
  },

  watch: {

    /**
     * Watch for when the swatches are defined.
     */
    linkedStyles: {
      handler() {

        /**
         * Check that swatch entities contain enough data to be considered "defined".
         * Placeholders (e.g., default objects with only a `url` property) should
         * still be treated as loading.
         */
        const swatchesPopulated = this.linkedStyles.every((swatch) => {
          return (
            swatch?.available ||
              Boolean(swatch?.colour) ||
              Boolean(swatch?.handle)
          )
        })

        if (!swatchesPopulated) {
          this.state = 'loading'

          return
        }

        const allHaveColour = this.linkedStyles.every(
          (swatch) => Boolean(swatch?.colour?.name),
        )
        const erroredStyles = this.linkedStyles.filter(
          (swatch) => !Boolean(swatch?.colour?.name),
        )

        /**
         * Add a small delay to ensure smooth element
         * transition between loading state and elements.
         */
        setTimeout(() => {
          if (!this.linkedStyles.length || !allHaveColour) {
            this.state = 'error'

            mint.Log(
              'warning',
              'Some linkedStyles failed to load swatch colour',
              false,
              {
                productHandle: this.productHandle,
                linkedStyles: this.linkedStyles,
                erroredStyles,
              },
            )

            return
          }

          this.state = 'ready'
        }, 250)
      },
      immediate: true,
    },
  },

  methods: {

    /**
     * Return a semantic button type.
     * @param {object} style - Swatch object.
     * @returns {string | Void}
     */
    getButtonType(style) {
      if (this.elementType === 'button' && style.url) {
        return 'button'
      }

      return null
    },

    /**
     * Return accessible alt text.
     * @param {object} style - Swatch object.
     * @returns {string | Void}
     */
    getImageAlt(style) {
      return style?.colour?.swatchAlt || null
    },

    /**
     * Return a disabled state for button elements. Used when swatch is active.
     * @param {object} style - Swatch object.
     * @returns {boolean | Void}
     */
    getDisabledState(style) {
      if (this.elementType !== 'button') {
        return null
      }

      return (this.productHandle === style?.handle)
        ? 'disabled'
        : null
    },

    /**
     * Return conditional CSS classes.
     * @param {object} style - Swatch object.
     * @returns {string | Void}
     */
    getSwatchClasses(style) {
      if (this.productHandle === style?.handle) {
        return 'is-active'
      }

      return null
    },

    /**
     * Return a unique key for the linkedStyles loop.
     * @param {object} style - Individual linkedStyle object item.
     * @param {number} index - For-loop index.
     * @returns {string}
     */
    getStyleKey(style, index) {
      if (!style || !style.handle) {
        return index
      }

      if (style.handle) {
        return style.url
      }

      return `${style.handle}-${style.colour.handle}-${style.styleId}`
    },

    /**
     * Return a hyperlink if the element is set to be a hyperlink.
     * @param {object} style - Swatch object.
     * @returns {string | Void}
     */
    getSwatchLink(style) {
      if (this.elementType === 'a' && style.url) {
        return style.url
      }

      return null
    },

    /**
     * Return an accessible button link text for a given swatch.
     * @param {object} style - Individual linkedStyle object item.
     * @returns {string}
     */
    getSwatchButtonText(style) {
      if (!style?.colour) {
        return ''
      }

      return mint.strings.products.swatchButtonText.replace(
        /{{ colour }}/g,
        style.colour.name,
      )
    },

    /**
     * Return a unique key for the linkedStyles loop.
     * @param {object} style - Individual linkedStyle object item.
     * @returns {string | Void}
     */
    getSwatchImageUrl(style) {

      /**
       * Prevent console error during loading state.
       */
      if (this.state === 'loading') {
        return null
      }

      const swatchUrl = style.colour?.swatch

      if (!swatchUrl) {
        mint.Log('warning', 'Swatch image missing for product', false, {
          styleId: style.styleId,
        })

        return null
      }

      /**
       * If the URL comes from Cloudinary, further formatting is not required.
       */
      if (swatchUrl.includes('cloudinary')) {
        return swatchUrl
      }

      return getSizedImageUrl(swatchUrl, '64x')
    },

    /**
     * Emit an event to the parent component notifying of a click.
     * @param {object} style - Individual linkedStyle object item.
     */
    handleSwatchClick(style) {
      this.$emit('swatch-image-clicked', style)
    },

    /**
     * Handle events for when a swatch is hovered.
     * - Emit an event to the parent.
     * - On mouseover, send current style
     * - On mouseleave, send blank style to indicate reset.
     * @param {object} [style] - Individual linkedStyle object item.
     */
    handleSwatchHover(style = false) {
      let hoveredSwatch = false

      if (style?.colour?.name) {
        hoveredSwatch = style.colour.name
      }

      this.$emit('swatch-image-hover', hoveredSwatch)
    },
  },
}
</script>
